import React from 'react';
import { logsAPI } from '../services/api';

/**
 * ErrorBoundary muy delgado: atrapa cualquier throw en el render tree, deja
 * el error en consola y pinta un fallback amigable en lugar de una pantalla
 * en blanco (que hasta hoy ocurria cuando la IA devolvia data inesperada).
 *
 * Uso: <ErrorBoundary onReset={...} category="cotizacion-form"><CotizacionForm .../></ErrorBoundary>
 */
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, info) {
        // Log para inspeccionar en devtools/producción — no rompemos el flujo.
        // eslint-disable-next-line no-console
        console.error('[ErrorBoundary] captured:', error, info);
        // Reporte a system_logs — asi los errores de la UI dejan de invisibilizarse.
        logsAPI.reportFrontend({
            level: 'error',
            category: this.props.category || 'error-boundary',
            mensaje: (error && error.message) || 'ErrorBoundary sin mensaje',
            stack: (error && error.stack) || null,
            contexto: {
                componentStack: info?.componentStack || null,
                label: this.props.label || null
            },
            url: typeof window !== 'undefined' ? window.location.href : null
        });
    }

    handleReset = () => {
        this.setState({ hasError: false, error: null });
        if (typeof this.props.onReset === 'function') this.props.onReset();
    };

    render() {
        if (!this.state.hasError) return this.props.children;

        const mensaje = this.state.error?.message || 'Ocurrió un error inesperado';
        const label = this.props.label || 'Algo se rompió al renderizar esta vista';

        return (
            <div style={{
                margin: '24px auto',
                maxWidth: 640,
                padding: 20,
                borderRadius: 12,
                border: '1px solid rgb(var(--danger) / 0.3)',
                background: 'rgb(var(--danger) / 0.08)',
                color: 'rgb(var(--text))'
            }}>
                <h3 style={{ margin: '0 0 8px 0', color: 'rgb(var(--danger))' }}>
                    {label}
                </h3>
                <p style={{ margin: '0 0 12px 0', fontSize: 13, color: 'rgb(var(--muted))' }}>
                    Los datos que se intentaban aplicar tenían un formato inesperado.
                    Puedes intentar recargar o volver atrás.
                </p>
                <pre style={{
                    margin: '0 0 16px 0',
                    padding: 10,
                    borderRadius: 6,
                    background: 'rgb(var(--surface-2) / 0.4)',
                    color: 'rgb(var(--muted))',
                    fontSize: 12,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word'
                }}>
                    {mensaje}
                </pre>
                <div style={{ display: 'flex', gap: 8 }}>
                    <button
                        type="button"
                        onClick={this.handleReset}
                        style={{
                            padding: '8px 14px',
                            borderRadius: 8,
                            border: '1px solid rgb(var(--border))',
                            background: 'rgb(var(--surface))',
                            color: 'rgb(var(--text))',
                            cursor: 'pointer',
                            fontWeight: 600
                        }}
                    >
                        Reintentar
                    </button>
                    <button
                        type="button"
                        onClick={() => { window.location.href = '/dashboard'; }}
                        style={{
                            padding: '8px 14px',
                            borderRadius: 8,
                            border: 'none',
                            background: 'rgb(var(--primary))',
                            color: '#fff',
                            cursor: 'pointer',
                            fontWeight: 600
                        }}
                    >
                        Ir al inicio
                    </button>
                </div>
            </div>
        );
    }
}

export default ErrorBoundary;
